
import type {
  ConflictApiError,
  FacetableFieldsDTO,
  FacetableRangeValuesDTO,
  FacetableSliderValuesDTO,
  FacetsDTO,
  FacetsService,
} from '@empathyco/playboard-core-domain'

import type { EndpointOption } from '../../composables'
import type { BrowseFacetValueDTO, FacetItem, FacetsInfoDto } from '../../models'
import type { FacetsInfoCrudService } from '../../services'

import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, defineComponent, getCurrentInstance, onUpdated, ref, watch } from 'vue'
import ConflictErrorModalWithOptions from '../../components/crud/conflict-error-modal/conflict-error-modal-with-options.component.vue'

import EpbFacetsAside from '../../components/facets/epb-facets-aside.component.vue'
import EpbFacetsContainer from '../../components/facets/epb-facets-container.component.vue'
import EpbFacetsModal from '../../components/facets/epb-facets-modal.component.vue'
import EpbFacetsSettingsSection from '../../components/facets/epb-facets-settings-section.component.vue'
import { UiConfirm } from '../../components/keen-ui'
import { EpbButton } from '../../components/shared/epb-button'
import { EpbKeywords } from '../../components/shared/epb-keywords'
import { EpbSelect } from '../../components/shared/epb-select'
import { EpbSwitch } from '../../components/shared/epb-switch'
import { EpbTextbox } from '../../components/shared/epb-textbox'
import FieldValueSelect from '../../components/shared/field-value-select.component.vue'

import ToolingFormLayout from '../../components/tooling/tooling-form-layout.component.vue'

import {
  useContextPrefill,
  useContextPrefillSource,
  useEndpoints,
  useFacets,
  useInstance,
  useToolingForm,
} from '../../composables'
import { isNonSortableFacetType } from '../../composables/use-facets'
import { CONTAINER } from '../../config'
import { TYPES } from '../../config/inversify.types'
import {
  EndpointContextEnum,
  FacetSortTypeEnum,
  FacetStatsUnitEnum,
  FacetTypeEnum,
  FeatureIdEnum,
  FilterIdEnum,
} from '../../enums'
import { capitalizeSentence } from '../../filters'
import { ResolveConflictOption } from '../../models'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { isBrowseLikeFacetsConfig } from '../../utils/facets-browse-context.utils'

export default defineComponent({
  name: 'FacetsForm',
  components: {
    FieldValueSelect,
    EpbSwitch,
    ConflictErrorModalWithOptions,
    EpbFacetsContainer,
    EpbButton,
    EpbFacetsAside,
    EpbFacetsModal,
    EpbFacetsSettingsSection,
    EpbKeywords,
    ToolingFormLayout,
    UiConfirm,
    EpbSelect,
    EpbTextbox,
  },
  setup(props, ctx) {
    const baseClass = 'facets-form'

    const currentInstance = getCurrentInstance()
    if (!currentInstance) {
      throw new Error("'ctx' is not defined")
    }

    const service = CONTAINER.get<FacetsService>(TYPES.FacetsService)

    const messages = currentInstance.proxy.$epb.messages.tools.facets

    const facetsUpdateRoute = ROUTER_CONSTS.routes[FeatureIdEnum.FACETS].update

    const updateMode =
      currentInstance.proxy.$route.name === ROUTER_CONSTS.routes[FeatureIdEnum.FACETS].update

    const isCreateMode = computed(() => !updateMode && !currentInstance.proxy.$route.params.id)

    const serviceFacetsInfo = CONTAINER.get<FacetsInfoCrudService>(TYPES.FacetsInfoCrudServide)

    const toolingForm = useToolingForm<FacetsDTO>(props, ctx, { messages, updateMode })
    toolingForm.initItem({ forceFetch: true })

    const {
      facetsOptions: options,
      attributeOptions,
      searchFacets,
      facetableFields: getFacetableFields,
    } = useFacets()

    const { instanceConfig } = useInstance()
    const { endpoints, defaultEndpointId } = useEndpoints()

    const endpointId = ref(toolingForm.item.value.endpointId ?? DomainEnum.SEARCH)
    const pendingEndpointId = ref<string | null>(null)

    const context = computed(
      () =>
        endpoints.value.find(endpoint => endpoint.value === endpointId.value)?.context ??
        EndpointContextEnum.QUERY,
    )

    const isBrowseLikeConfig = computed(() =>
      isBrowseLikeFacetsConfig(toolingForm.item.value, context.value),
    )

    const confirmCriteriaChangeModal = ref()

    const attributeOption = ref(toolingForm.item.value.criteria.context?.[0]?.values[0] ?? '')

    const facetsModal = ref()
    const facetsValues = ref()

    const isOpenFacetsAside = ref(false)
    const facetSettingItemSelected = ref()

    const facetsOptionsContainer = ref([])
    const facetsOrder = ref(toolingForm.item.value.config.facets ?? [])

    const resolveConflictModal = ref()
    const saveConflictApiError = ref<ConflictApiError>(undefined)

    const facetsOptions = ref([])
    const facetableFieldsDTO = ref<FacetableFieldsDTO>()

    const matchAll = ref(!!toolingForm.item.value.criteria.context?.[0]?.values.length)

    const listFacetsValuesMetadata = ref([])

    const inheritance = ref(false)

    const facetsSelected = computed(() =>
      Object.values(toolingForm.item.value.config.facetableFields)
        ?.filter(facetsType => !!facetsType) // Filter undefined facetableField
        .flatMap(facetsType => facetsType?.map(facet => facet?.field)),
    )

    const metadata = computed(() => {
      const result =
        listFacetsValuesMetadata.value
          .filter(
            facetValueMetadata => facetValueMetadata.facet === facetSettingItemSelected.value.title,
          )
          .flatMap(facetValueMetadata => {
            return facetValueMetadata.metadata.map(item => {
              return {
                title: item.key,
                subtitle: item.value,
              }
            })
          }) ?? []

      return result.filter(
        (item, index, array) =>
          index === array.findIndex(t => t.title === item.title && t.subtitle === item.subtitle),
      )
    })

    const hasPendingMetadataForSelectedFacet = computed(
      () =>
        !!facetSettingItemSelected.value?.title &&
        listFacetsValuesMetadata.value.some(f => f.facet === facetSettingItemSelected.value?.title),
    )

    const filters = computed(() => {
      let filters = toolingForm.visibleItemPivots.value
      if (!filters.lang) {
        const langConfig = toolingForm.pivotsConfig.value.lang
        const selectedValues = langConfig.values.map(value => value.id)
        filters = {
          lang: {
            config: langConfig,
            selectedValues,
          },
          ...filters,
        }
      }
      if (instanceConfig.value.allowOnlyMandatoryFilters) {
        Object.keys(filters).forEach(key => {
          if (filters[key].config) {
            filters[key].config.multiselectDisabled = true
            filters[key].config.mandatory = true
          }
        })
      } else if (filters.lang?.config) {
        filters.lang.config.mandatory = true
        filters.lang.config.multiselectDisabled = true
      }
      return filters
    })

    const facetSelected = computed(() => {
      return toolingForm.item.value.criteria.context?.[1]?.values[0]
    })

    const isDisabledSettings = computed((): boolean => {
      return (
        !toolingForm.item.value.endpointId ||
        !filters.value.lang.selectedValues.length ||
        (isBrowseLikeConfig.value &&
          toolingForm.item.value.endpointId === 'browse' &&
          attributeOption.value &&
          !facetSelected.value)
      )
    })

    const facetableFields = computed(() => {
      const config = toolingForm.item.value.config

      if (facetsValues.value) {
        config.facetableFields = {
          hierarchicalFacets: [],
          valueFacets: [],
          rangeFacets: [],
          booleanFacets: [],
          sliderFacets: [],
        }
      }

      const getFacetConfig = (facetType: FacetTypeEnum, facet) => {
        const facetsConfigByType = {
          [FacetTypeEnum.HIERARCHICAL]: facetableFieldsDTO.value?.hierarchicalFacets,
          [FacetTypeEnum.VALUE]: facetableFieldsDTO.value?.valueFacets,
          [FacetTypeEnum.RANGE]: facetableFieldsDTO.value?.rangeFacets,
          [FacetTypeEnum.BOOLEAN]: facetableFieldsDTO.value?.booleanFacets,
          [FacetTypeEnum.SLIDER]: facetableFieldsDTO.value?.sliderFacets,
        }[facetType]

        return facetsConfigByType?.find(
          ({ field, filterField }) => (filterField ?? field) === facet.label,
        )
      }

      const addFacet = (facets, facet, facetType: FacetTypeEnum): void => {
        const facetExists = facets.some(({ field }) => field === facet.label)
        if (facetExists) return

        const facetConfig = getFacetConfig(facetType, facet)

        const baseFacetFields = {
          field: facet.label,
          prettyName: facet.prettyName ?? '',
          filterField: facet.filter,
        }

        const queryAggregationStrategyId = facetConfig?.queryAggregationStrategyId

        const facetTypeConfig = (() => {
          switch (facetType) {
            case FacetTypeEnum.RANGE:
              return { ranges: (facetConfig as FacetableRangeValuesDTO)?.ranges }
            case FacetTypeEnum.BOOLEAN:
              return {}
            case FacetTypeEnum.SLIDER: {
              const sliderConfig = facetConfig as FacetableSliderValuesDTO | undefined
              const decimalDigits = facet.decimalDigits ?? sliderConfig?.decimalDigits
              return {
                unit: facet.unit ?? sliderConfig?.unit ?? FacetStatsUnitEnum.CURRENCY,
                ...(decimalDigits !== undefined && decimalDigits !== null
                  ? { decimalDigits: Number(decimalDigits) }
                  : {}),
              }
            }
            default:
              return { sortType: facet.sortType ?? FacetSortTypeEnum.ALPHABETICALLY }
          }
        })()

        facets.push({
          ...baseFacetFields,
          ...(queryAggregationStrategyId && { queryAggregationStrategyId }),
          ...facetTypeConfig,
        })
      }

      facetsValues.value?.forEach(facet => {
        switch (facet.type) {
          case FacetTypeEnum.HIERARCHICAL:
            addFacet(config.facetableFields.hierarchicalFacets, facet, FacetTypeEnum.HIERARCHICAL)
            break
          case FacetTypeEnum.VALUE:
            addFacet(config.facetableFields.valueFacets, facet, FacetTypeEnum.VALUE)
            break
          case FacetTypeEnum.RANGE:
            addFacet(config.facetableFields.rangeFacets, facet, FacetTypeEnum.RANGE)
            break
          case FacetTypeEnum.BOOLEAN:
            addFacet(config.facetableFields.booleanFacets, facet, FacetTypeEnum.BOOLEAN)
            break
          case FacetTypeEnum.SLIDER:
            addFacet(config.facetableFields.sliderFacets, facet, FacetTypeEnum.SLIDER)
            break
        }
      })

      return config.facetableFields
    })

    const facetsSettingsItems = computed((): FacetItem[] => {
      const results = []
      let index = facetsOrder.value.length

      const addFacetToResults = (facet: any, facetType?: FacetTypeEnum): void => {
        const itemOrderIndex = facetsOrder.value?.findIndex(f => f === facet.field)
        const itemFacetValue = facetsValues.value?.findIndex(f => f.label === facet.field)
        const resolvedFacetType =
          facetType ?? facetsValues.value?.find(f => f.label === facet.field)?.type
        const isNonSortable = isNonSortableFacetType(resolvedFacetType ?? '')

        const facetItem: FacetItem = {
          title: facet.field,
          subtitle: isNonSortable ? undefined : facet.sortType || FacetSortTypeEnum.CUSTOM,
          prettyName: facet.prettyName ?? '',
          ...(resolvedFacetType === FacetTypeEnum.SLIDER
            ? {
                unit: facet.unit ?? FacetStatsUnitEnum.CURRENCY,
                decimalDigits: facet.decimalDigits,
              }
            : {}),
        }

        if (itemOrderIndex !== -1) {
          results[itemOrderIndex] = facetItem
        } else if (itemFacetValue !== -1) {
          results[index] = facetItem
          index++
        }
      }

      facetableFields.value.hierarchicalFacets?.forEach(facet =>
        addFacetToResults(facet, FacetTypeEnum.HIERARCHICAL),
      )
      facetableFields.value.valueFacets?.forEach(facet =>
        addFacetToResults(facet, FacetTypeEnum.VALUE),
      )
      facetableFields.value.rangeFacets?.forEach(facet =>
        addFacetToResults(facet, FacetTypeEnum.RANGE),
      )
      facetableFields.value.booleanFacets?.forEach(facet =>
        addFacetToResults(facet, FacetTypeEnum.BOOLEAN),
      )
      facetableFields.value.sliderFacets?.forEach(facet =>
        addFacetToResults(facet, FacetTypeEnum.SLIDER),
      )

      return results
    })

    const isValid = computed(() => {
      const endpointContext = endpoints.value.find(
        endpoint => endpoint.value === endpointId.value,
      )?.context
      const hasFacetsSettings = facetsSettingsItems?.value.length > 0
      const isQueryContext = endpointContext === EndpointContextEnum.QUERY
      const hasContextValues = toolingForm.item.value.criteria.context?.[0]?.values.length > 0
      const isMatchAllWithValues = matchAll.value && hasContextValues
      const isNotMatchAllWithoutValues = !matchAll.value && !hasContextValues
      const hasInvalidQueryCriteria =
        isQueryContext && (isMatchAllWithValues || isNotMatchAllWithoutValues)

      return toolingForm.isValid.value && hasFacetsSettings && !hasInvalidQueryCriteria
    })

    const updateFacetsOptionsContainer = (): void => {
      const facetSettingsMap = new Map(
        facetsSettingsItems.value.map(item => [item.title, item.prettyName]),
      )

      facetsOptionsContainer.value = facetsOptions.value
        ?.map(facetOption => {
          const title = facetSettingsMap.has(facetOption.label)
          const prettyName = facetSettingsMap.get(facetOption.label)
          return title ? { ...facetOption, prettyName: prettyName ?? null } : null
        })
        .filter(Boolean)
        .sort((a, b) => {
          const indexA = facetsOrder.value.indexOf(a.label)
          const indexB = facetsOrder.value.indexOf(b.label)

          if (indexA === -1) {
            return 1
          }
          if (indexB === -1) {
            return -1
          }
          return indexA - indexB
        })
    }

    const closeAside = (): void => {
      isOpenFacetsAside.value = false
      facetsSettingsItems.value.forEach(facetItem => {
        if (facetItem.selected) {
          facetItem.selected = false
        }
      })
    }

    const buildFacetsInfoCriteria = (): FacetsInfoDto['criteria'] => [
      { key: 'endpoint', values: [endpointId.value] },
      ...Object.values(filters.value)
        .filter(pivot => pivot.selectedValues.length)
        .map(pivot => ({ key: pivot.config.id, values: pivot.selectedValues })),
      ...(toolingForm.item.value.criteria.context?.map(item => ({
        key: item.name,
        values: item.name === 'browseValue' ? [item.values[0]] : item.values,
      })) ?? []),
      { key: 'inheritance', values: [inheritance.value.toString()] },
    ]

    const updateMetadata = (metadataParam: FacetItem[], listFacetsValuesMetadataParam): void => {
      listFacetsValuesMetadataParam.results[0].metadata = metadataParam.map(item => {
        return { key: item.title, value: item.subtitle }
      })
      listFacetsValuesMetadataParam.results.forEach(facetValue => {
        const existFacet = listFacetsValuesMetadata.value.findIndex(
          value => facetValue.facet === value.facet,
        )
        if (existFacet === -1) {
          listFacetsValuesMetadata.value.push(facetValue)
        } else {
          listFacetsValuesMetadata.value[existFacet] = facetValue
        }
      })
    }

    const updateValues = (values: string[]): void => {
      toolingForm.item.value.criteria = {
        ...toolingForm.item.value.criteria,
        context: [{ name: '', values }],
      }
    }

    const updateFacetValues = (newOrder: BrowseFacetValueDTO[], label: string): void => {
      toolingForm.item.value.config.facetableFields.hierarchicalFacets?.forEach(
        hierarchicalFacetItem => {
          if (hierarchicalFacetItem.field === label) {
            hierarchicalFacetItem.rankings = newOrder.map(facetValue => ({
              value: facetValue.id,
              action: 'BOOST',
            }))
            hierarchicalFacetItem.sortType = FacetSortTypeEnum.CUSTOM
          }
        },
      )
      toolingForm.item.value.config.facetableFields.valueFacets?.forEach(valueFacet => {
        if (valueFacet.field === label) {
          valueFacet.rankings = newOrder.map(facetValue => ({
            value: facetValue.value,
            action: 'BOOST',
          }))
          valueFacet.sortType = FacetSortTypeEnum.CUSTOM
        }
      })
    }

    const submitAside = async (itemToSubmit: FacetItem): Promise<void> => {
      facetsValues.value = facetsValues.value?.map(value => {
        if (itemToSubmit.title === value.label) {
          return {
            ...value,
            prettyName: itemToSubmit.prettyName ?? '',
            ...(isNonSortableFacetType(value.type)
              ? {}
              : { sortType: itemToSubmit.subtitle ?? '' }),
            ...(value.type === FacetTypeEnum.SLIDER
              ? {
                  unit: itemToSubmit.unit ?? FacetStatsUnitEnum.CURRENCY,
                  decimalDigits: itemToSubmit.decimalDigits,
                }
              : {}),
          }
        } else {
          return { ...value }
        }
      })

      toolingForm.item.value.config.facetableFields.hierarchicalFacets?.forEach(
        hierarchicalFacetItem => {
          if (hierarchicalFacetItem.field === itemToSubmit.title) {
            hierarchicalFacetItem.prettyName = itemToSubmit.prettyName ?? ''
            hierarchicalFacetItem.sortType = itemToSubmit.subtitle
          }
        },
      )
      toolingForm.item.value.config.facetableFields.valueFacets?.forEach(valueFacetsItem => {
        if (valueFacetsItem.field === itemToSubmit.title) {
          valueFacetsItem.prettyName = itemToSubmit.prettyName ?? ''
          valueFacetsItem.sortType = itemToSubmit.subtitle
        }
      })
      toolingForm.item.value.config.facetableFields.rangeFacets?.forEach(rangeFacetsItem => {
        if (rangeFacetsItem.field === itemToSubmit.title) {
          rangeFacetsItem.prettyName = itemToSubmit.prettyName ?? ''
        }
      })
      toolingForm.item.value.config.facetableFields.booleanFacets?.forEach(booleanFacetsItem => {
        if (booleanFacetsItem.field === itemToSubmit.title) {
          booleanFacetsItem.prettyName = itemToSubmit.prettyName ?? ''
        }
      })
      toolingForm.item.value.config.facetableFields.sliderFacets?.forEach(sliderFacetsItem => {
        if (sliderFacetsItem.field === itemToSubmit.title) {
          sliderFacetsItem.prettyName = itemToSubmit.prettyName ?? ''
          sliderFacetsItem.unit =
            (itemToSubmit.unit as FacetableSliderValuesDTO['unit']) ?? FacetStatsUnitEnum.CURRENCY
          if (itemToSubmit.decimalDigits === undefined || itemToSubmit.decimalDigits === null) {
            delete sliderFacetsItem.decimalDigits
          } else {
            sliderFacetsItem.decimalDigits = Number(itemToSubmit.decimalDigits)
          }
        }
      })

      updateFacetsOptionsContainer()
      closeAside()
    }

    const addFacetsSettings = (values): void => {
      facetsValues.value = values.map(value => {
        const valueTypeFacets = {
          [FacetTypeEnum.HIERARCHICAL]: facetableFields.value.hierarchicalFacets,
          [FacetTypeEnum.VALUE]: facetableFields.value.valueFacets,
          [FacetTypeEnum.RANGE]: facetableFields.value.rangeFacets,
          [FacetTypeEnum.BOOLEAN]: facetableFields.value.booleanFacets,
          [FacetTypeEnum.SLIDER]: facetableFields.value.sliderFacets,
        }[value.type]
        const matchedFacet = valueTypeFacets?.find(facet => facet.field === value.label)
        const { prettyName, sortType } = matchedFacet ?? {}
        const sliderFacet = matchedFacet as FacetableSliderValuesDTO | undefined
        return {
          ...value,
          prettyName,
          sortType,
          ...(value.type === FacetTypeEnum.SLIDER
            ? {
                unit: sliderFacet?.unit,
                decimalDigits: sliderFacet?.decimalDigits,
              }
            : {}),
        }
      })
      const valueLabels = values.map(value => value.label)

      facetsOrder.value = facetsOrder.value.filter(facet => valueLabels.includes(facet))

      valueLabels.forEach(label => {
        if (!facetsOrder.value.includes(label)) {
          facetsOrder.value.push(label)
        }
      })
    }

    const onSetEnabled = (value: boolean): void => {
      toolingForm.item.value.criteria.enabled = value
    }

    const onCancel = (): void => {
      listFacetsValuesMetadata.value = []
      toolingForm.onCancel()
    }

    const applyDefaultBrowseFields = (endpoint: EndpointOption): void => {
      if (
        endpoint.context === EndpointContextEnum.FILTER &&
        endpoint.defaultBrowseField &&
        endpoint.defaultBrowseValue
      ) {
        toolingForm.item.value.criteria.context = [
          { name: '', values: [endpoint.defaultBrowseField] },
          { name: 'browseValue', values: [endpoint.defaultBrowseValue] },
        ]
      }
    }

    const selectEndpoint = (value: string): void => {
      const selectedEndpoint = endpoints.value.find(endpoint => endpoint.value === value)
      if (toolingForm.item.value.endpointId && selectedEndpoint?.context !== context.value) {
        pendingEndpointId.value = value
        confirmCriteriaChangeModal.value.open()
      } else {
        endpointId.value = value
        toolingForm.item.value.endpointId = value
        toolingForm.item.value.config.context =
          (selectedEndpoint?.context ?? EndpointContextEnum.QUERY) === EndpointContextEnum.QUERY
            ? DomainEnum.SEARCH.toUpperCase()
            : DomainEnum.BROWSE.toUpperCase()
        if (selectedEndpoint) {
          applyDefaultBrowseFields(selectedEndpoint)
        }
      }
    }

    const applyDefaultEndpoint = (): void => {
      if (updateMode || toolingForm.item.value.endpointId) {
        return
      }

      const defaultEndpoint = endpoints.value.find(
        endpoint => endpoint.value === defaultEndpointId.value,
      )
      if (!defaultEndpoint) {
        return
      }

      toolingForm.item.value.endpointId = defaultEndpoint.value
      toolingForm.item.value.config.context =
        defaultEndpoint.context === EndpointContextEnum.QUERY
          ? DomainEnum.SEARCH.toUpperCase()
          : DomainEnum.BROWSE.toUpperCase()

      applyDefaultBrowseFields(defaultEndpoint)
    }

    const onMatchAllChange = (value: boolean): void => {
      if (toolingForm.item.value.endpointId === DomainEnum.SEARCH && value) {
        toolingForm.item.value.criteria.context[0].values = []
      }
      matchAll.value = value
    }

    const selectAttribute = (value: string | undefined): void => {
      if (value !== attributeOption.value) {
        facetsOrder.value = toolingForm.item.value.config.facets ?? []
        facetsValues.value = []
        if (toolingForm.item.value.criteria.context?.[1] !== undefined) {
          toolingForm.item.value.criteria.context[1].values = []
        }
      }
      attributeOption.value = value
      if (value === undefined) {
        toolingForm.item.value.criteria.context = []
        return
      }
      if (
        !toolingForm.item.value.criteria.context ||
        !toolingForm.item.value.criteria.context.length
      ) {
        toolingForm.item.value.criteria.context = [{ name: value, values: [] }]
      }
      toolingForm.item.value.criteria.context[0].values[0] = attributeOption.value
    }

    const addMetadata = (): void => {
      listFacetsValuesMetadata.value.forEach(async facetInfoMetadata => {
        const facet = await serviceFacetsInfo.readFacet(facetInfoMetadata ?? undefined)
        if (facet.id) {
          if (facetInfoMetadata.metadata.length) {
            await serviceFacetsInfo.update(facetInfoMetadata)
          } else {
            await serviceFacetsInfo.delete(facetInfoMetadata)
          }
        } else {
          await serviceFacetsInfo.create({
            id: undefined,
            facet: facetInfoMetadata.facet,
            metadata: facetInfoMetadata.metadata,
            criteria: buildFacetsInfoCriteria() ?? [],
            instance: instanceConfig.value.id,
          })
        }
      })
    }

    const removeCustomSortType = facets => {
      facets?.forEach(facet => {
        if (facet.sortType === FacetSortTypeEnum.CUSTOM) {
          delete facet.sortType
        }
      })
    }

    const onAccept = (): void => {
      if (
        toolingForm.item.value.endpointId === DomainEnum.SEARCH ||
        !toolingForm.item.value.criteria.context?.[1]?.values?.length
      ) {
        if (
          toolingForm.item.value.criteria.context?.[0]?.values.length &&
          toolingForm.item.value.endpointId === DomainEnum.SEARCH
        ) {
          toolingForm.item.value.criteria.context[0].name = 'query'
          if (toolingForm.item.value.criteria.context[1]) {
            toolingForm.item.value.criteria.context.pop()
          }
        } else {
          toolingForm.item.value.criteria.context = undefined
        }
      } else if (toolingForm.item.value.criteria.context[1]?.values?.length) {
        toolingForm.item.value.criteria.context[0].name = 'browseField'
        toolingForm.item.value.criteria.context[1].values = [
          toolingForm.item.value.criteria.context[1].values[0],
        ]
        toolingForm.item.value.criteria.inheritance = inheritance.value
      }

      let facets
      if (facetsOrder.value.length === facetsSelected.value.length) {
        facets = facetsOrder.value
      } else if (facetsOrder.value.length) {
        const facetsOrderSet = new Set(facetsOrder.value)
        facetsSelected.value.forEach(facet => facetsOrderSet.add(facet))
        facets = Array.from(facetsOrderSet)
      } else {
        facets = facetsSelected.value
      }

      removeCustomSortType(facetableFields.value.hierarchicalFacets)
      removeCustomSortType(facetableFields.value.valueFacets)

      const contextEndpoint =
        endpoints.value.find(endpoint => endpoint.value === endpointId.value)?.context ??
        EndpointContextEnum.QUERY
      toolingForm.item.value.config = {
        context:
          contextEndpoint === EndpointContextEnum.QUERY
            ? DomainEnum.SEARCH.toUpperCase()
            : DomainEnum.BROWSE.toUpperCase(),
        enabled: true,
        facetableFields: facetableFields.value,
        facets,
        mainCollectionId: instanceConfig.value.id,
        secondaryCollectionId: instanceConfig.value.id,
        facetingProvidersDisabled: true,
      }
      addMetadata()
      toolingForm.onAccept({ errorHandler })
    }

    const filterFacetsByAvailableLabels = (availableFacetLabels: string[]): void => {
      facetsOrder.value = facetsOrder.value.filter(facet => availableFacetLabels.includes(facet))

      if (facetsValues.value) {
        facetsValues.value = facetsValues.value.filter(facet =>
          availableFacetLabels.includes(facet.label),
        )
      }

      const facetableFields = toolingForm.item.value.config.facetableFields
      const filterFacets = (facets: any[]) =>
        facets.filter(facet => availableFacetLabels.includes(facet.field))

      facetableFields.hierarchicalFacets = filterFacets(facetableFields.hierarchicalFacets)
      facetableFields.valueFacets = filterFacets(facetableFields.valueFacets)
      facetableFields.rangeFacets = filterFacets(facetableFields.rangeFacets)
      facetableFields.booleanFacets = filterFacets(facetableFields.booleanFacets ?? [])
      facetableFields.sliderFacets = filterFacets(facetableFields.sliderFacets ?? [])
    }

    const selectFacet = async (value: string): Promise<void> => {
      if (!value) {
        if (toolingForm.item.value.criteria.context[1] !== undefined) {
          toolingForm.item.value.criteria.context[1].values = []
        }
        return
      }

      const facets = [value]
      const context = toolingForm.item.value.criteria.context

      if (context[1] !== undefined) {
        context[1].name = 'browseValue'
        context[1].values = facets

        await searchFacets(toolingForm.item.value, filters.value)
        const availableFacetLabels = options.value.map(option => option.label)
        filterFacetsByAvailableLabels(availableFacetLabels)
      } else {
        context.push({
          name: 'browseValue',
          values: facets,
        })
      }
    }

    const confirmCriteriaChange = (): void => {
      try {
        if (pendingEndpointId.value) {
          const pendingEndpoint = endpoints.value.find(
            endpoint => endpoint.value === pendingEndpointId.value,
          )
          toolingForm.item.value.criteria.context = [{ name: '', values: [] }]
          toolingForm.item.value.endpointId = pendingEndpointId.value
          endpointId.value = pendingEndpointId.value
          pendingEndpointId.value = null
          if (pendingEndpoint) {
            applyDefaultBrowseFields(pendingEndpoint)
          }
        }
        facetsOrder.value = []
        facetsValues.value = []
        facetsOptionsContainer.value = []
      } catch (error) {
        currentInstance.proxy.$emit(
          'showError',
          currentInstance.proxy.$epb.utils.decodeErrorMessage(error),
        )
      }
    }
    const selectFacetSettingItem = (itemToSelect: FacetItem): void => {
      isOpenFacetsAside.value = true
      facetSettingItemSelected.value = itemToSelect
    }

    const removeFacetSettingItem = (itemToRemove: FacetItem): void => {
      const removeItem = (items = [], condition) => {
        const index = items.findIndex(condition)
        if (index !== -1) {
          items.splice(index, 1)
        }
      }

      if (facetsValues.value) {
        removeItem(facetsValues.value, item => item.label === itemToRemove.title)
      }

      const facetableFields = toolingForm.item.value.config.facetableFields

      removeItem(facetableFields.hierarchicalFacets, ({ field }) => field === itemToRemove.title)
      removeItem(facetableFields.valueFacets, ({ field }) => field === itemToRemove.title)
      removeItem(facetableFields.rangeFacets, ({ field }) => field === itemToRemove.title)
      removeItem(facetableFields.booleanFacets, ({ field }) => field === itemToRemove.title)
      removeItem(facetableFields.sliderFacets, ({ field }) => field === itemToRemove.title)

      facetsOrder.value = facetsOrder.value.filter(facet => facet !== itemToRemove.title)
    }

    const orderFacetsSettingItems = (facetItemsOrder: string[]): void => {
      facetsOrder.value = facetItemsOrder
    }

    async function onResolveConflict(resolveConflictOption: ResolveConflictOption): Promise<void> {
      try {
        if (resolveConflictOption === ResolveConflictOption.DISABLE_PREVIOUS) {
          const updatePromises = saveConflictApiError.value.conflictingElements.map(
            async itemWithConflict => {
              const item = await service.read(itemWithConflict.id)
              item.criteria.enabled = false
              return service.update(item)
            },
          )

          await Promise.all(updatePromises)
        } else {
          toolingForm.item.value.criteria.enabled = false
        }
      } finally {
        toolingForm.onAccept()
      }
    }

    function errorHandler(error: ConflictApiError): void {
      saveConflictApiError.value = error
      resolveConflictModal.value.open()
    }

    const {
      selectedFiltersValues,
      endpointId: contextEndpointId,
      contextBrowseField: contextAttributeOption,
      contextBrowseValue: contextFacetSelected,
    } = useContextPrefillSource()

    const { setupContextPrefillWatch } = useContextPrefill()

    setupContextPrefillWatch({
      mode: 'facet',
      isCreateMode,
      toolingForm,
      selectedFiltersValues,
      contextAttributeOption,
      contextFacetSelected,
      contextEndpointId,
      endpoints,
      endpointId,
      contextBrowseField: attributeOption,
    })

    watch(
      () => toolingForm.item.value.endpointId,
      () => {
        endpointId.value = toolingForm.item.value.endpointId ?? DomainEnum.SEARCH
      },
      { immediate: true },
    )

    watch(() => defaultEndpointId.value, applyDefaultEndpoint, { immediate: true })

    watch(
      [isBrowseLikeConfig, () => toolingForm.item.value.criteria?.context?.[0]?.values?.[0]],
      () => {
        if (isBrowseLikeConfig.value) {
          attributeOption.value = toolingForm.item.value.criteria.context?.[0]?.values?.[0] ?? ''
          return
        }

        const contextValues = toolingForm.item.value.criteria?.context?.[0]?.values
        if (contextValues) {
          matchAll.value = !contextValues.length
        }
      },
      { immediate: true },
    )

    watch(
      () => toolingForm.item.value.config.facets,
      () => {
        facetsOrder.value = toolingForm.item.value.config.facets ?? []
      },
      { immediate: true },
    )

    watch(
      [facetsOptions, facetsSettingsItems, facetsOrder, facetsValues],
      updateFacetsOptionsContainer,
      {
        immediate: true,
      },
    )

    watch(
      () => toolingForm.item.value.criteria.inheritance,
      newValue => (inheritance.value = newValue ?? false),
      { immediate: true },
    )

    watch(
      filters,
      () => {
        if (
          filters.value.lang.config.values.length === 1 &&
          !toolingForm.item.value.criteria.pivots.find(pivot => pivot.name === FilterIdEnum.LANG)
        ) {
          toolingForm.item.value.criteria.pivots.push({
            name: filters.value.lang.config.id,
            values: filters.value.lang.selectedValues,
          })
        }
      },
      { immediate: true },
    )

    onUpdated(async () => {
      if (filters.value.lang.selectedValues[0]) {
        try {
          await searchFacets(toolingForm.item.value, filters.value, true)
        } catch {
          await searchFacets(toolingForm.item.value, filters.value)
        }
        facetsOptions.value = options.value
        if (!facetableFieldsDTO.value) {
          facetableFieldsDTO.value = await getFacetableFields()
        }
      }
    })

    return {
      ...toolingForm,
      DomainEnum,
      baseClass,
      messages,
      facetsUpdateRoute,
      updateMode,
      endpoints,
      endpointId,
      context,
      confirmCriteriaChangeModal,
      attributeOption,
      facetsModal,
      isOpenFacetsAside,
      matchAll,
      inheritance,
      filters,
      isValid,
      isDisabledSettings,
      facetSelected,
      attributeOptions,
      facetsSettingsItems,
      facetsOrder,
      facetsOptionsContainer,
      facetSettingItemSelected,
      resolveConflictModal,
      saveConflictApiError,
      metadata,
      hasPendingMetadataForSelectedFacet,
      selectFacet,
      updateValues,
      updateFacetValues,
      submitAside,
      closeAside,
      updateMetadata,
      addFacetsSettings,
      onSetEnabled,
      selectEndpoint,
      onMatchAllChange,
      selectAttribute,
      onCancel,
      onAccept,
      capitalizeSentence,
      confirmCriteriaChange,
      selectFacetSettingItem,
      removeFacetSettingItem,
      orderFacetsSettingItems,
      onResolveConflict,
    }
  },
})
